import { useCallback, useRef, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, isOwner, isTaskNameChanged, taskStatusByCode } from 'entities';

import { Skeleton, Row, Tooltip, Input } from 'components/Blocks';
import { PencilIcon } from 'components/Icons';

import { TaskLink } from './TaskLink';

import { useTaskModal } from '../context.taskModal';
import { useToggle, useUser, useWatch } from 'hooks';

import { cn } from 'styles';

export function TaskName() {
  const { t } = useLang();
  const loggedUser = useUser();

  const inputRef = useRef<HTMLInputElement>(null);

  const { task, updateTask } = useTaskModal();

  const [name, setName] = useState(task.name);
  useWatch(name, setName);

  const { isOn, turnOn, turnOff } = useToggle();

  const { canChangeName } = taskStatusByCode[task.status];

  const canEdit = [isOwner(task, loggedUser), canChangeName].every(Boolean);

  const { projectId, id } = task;

  const openInput = useCallback(() => {
    turnOn();

    setTimeout(() => inputRef.current?.focus(), 50);
  }, [turnOn]);

  const closeInput = useCallback(() => {
    turnOff();

    setTimeout(() => inputRef.current?.blur(), 50);
  }, [turnOff]);

  const changeName = actions.projects.changeTaskName.use({
    onSuccess: () => {
      updateTask({ name: name.trim() });

      closeInput();
    },
  });

  const handleChange = async () => {
    const newName = name.trim();

    if (!newName || newName.length <= 4 || newName === task.name) {
      setName(task.name);

      return closeInput();
    }

    await changeName.execute({
      project: projectId,
      task: id,
      name: newName,
    });
  };

  if (changeName.loading) return <Skeleton width="70%" height={40} />;

  return (
    <Row gap="2">
      <TaskLink />

      {isOn ? (
        <Input
          ref={inputRef}
          value={name}
          onKeyDown={({ key }) => key === 'Enter' && handleChange()}
          onChange={isOn ? ({ target }) => setName(target.value) : undefined}
          onBlur={handleChange}
          disabled={!canEdit}
          className="h-auto w-full py-1"
        />
      ) : (
        <span>
          <Tooltip
            disabled={!canEdit}
            content={
              <Row gap="2">
                <PencilIcon size={16} />
                <span className="font-medium">{t('edit')}</span>
              </Row>
            }
          >
            <span
              onClick={canEdit ? openInput : undefined}
              className={cn(
                'text-xl font-semibold text-primary transition-opacity dark:text-card-foreground phone:text-lg',
                canEdit && 'cursor-pointer hover:opacity-80',
              )}
            >
              {name}
            </span>
          </Tooltip>

          {isTaskNameChanged(task) && (
            <span className="ml-2 text-xs italic">{` (${task.baseName})`}</span>
          )}
        </span>
      )}
    </Row>
  );
}
